import React, { useState, useEffect, useCallback } from 'react';
import { apiService } from '../services/api';
import ErrorBoundary from '../components/ErrorBoundary';
import './SpecialitatiNew.css';

function SpecialitatiNew() {
    // Data and loading states
    const [specialties, setSpecialties] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [pagination, setPagination] = useState(null);

    // Search and filter states
    const [searchQuery, setSearchQuery] = useState('');
    const [sortOrder, setSortOrder] = useState('denumire'); // 'denumire' or '-denumire'
    const [page, setPage] = useState(1);
    const [perPage] = useState(12); // Show more items per page for specialties

    const loadSpecialties = useCallback(async () => {
        try {
            setLoading(true);
            setError('');
            const params = {
                q: searchQuery || undefined,
                sort: sortOrder,
                page: page,
                per_page: perPage
            };

            // Remove undefined values
            Object.keys(params).forEach(key => {
                if (params[key] === undefined) {
                    delete params[key];
                }
            });

            const response = await apiService.getSpsSpecialties(params);

            if (response.ok) {
                setSpecialties(response.items || []);
                setPagination(response.meta || null);
            } else {
                setError('Eroare la încărcarea specialităților. Vă rugăm să reîncercați.');
                setSpecialties([]);
                setPagination(null);
            }
        } catch (err) {
            console.error('Error loading specialties:', err);
            setError('Eroare la încărcarea specialităților. Vă rugăm să reîncercați.');
            setSpecialties([]);
            setPagination(null);
        } finally {
            setLoading(false);
        }
    }, [searchQuery, sortOrder, page, perPage]);

    // Load specialties on component mount and when filters change
    useEffect(() => {
        loadSpecialties();
    }, [loadSpecialties]);

    useEffect(() => {
        setPage(1); // Reset to first page on filter change
    }, [searchQuery, sortOrder]);

    const handleSearch = (e) => {
        setSearchQuery(e.target.value);
    };

    const handleSortChange = (newSort) => {
        setSortOrder(newSort);
    };

    const handlePageChange = (newPage) => {
        if (newPage >= 1 && newPage <= (pagination?.last_page || 1)) {
            setPage(newPage);
        }
    };

    const clearFilters = () => {
        setSearchQuery('');
        setSortOrder('denumire');
        setPage(1);
    };

    const renderPageNumbers = () => {
        const pages = [];
        if (!pagination) return pages;

        const { page: currentPage, last_page } = pagination;

        // Always show first page
        pages.push(
            <button
                key={1}
                onClick={() => handlePageChange(1)}
                style={{
                    ...pageButtonStyle,
                    backgroundColor: currentPage === 1 ? 'var(--color-primary)' : '#f8f9fa',
                    color: currentPage === 1 ? 'white' : '#333'
                }}
            >
                1
            </button>
        );

        if (last_page <= 5) {
            // Show all pages if 5 or less
            for (let i = 2; i <= last_page; i++) {
                pages.push(
                    <button
                        key={i}
                        onClick={() => handlePageChange(i)}
                        style={{
                            ...pageButtonStyle,
                            backgroundColor: currentPage === i ? 'var(--color-primary)' : '#f8f9fa',
                            color: currentPage === i ? 'white' : '#333'
                        }}
                    >
                        {i}
                    </button>
                );
            }
        } else {
            // Show ellipsis and current area
            if (currentPage > 3) {
                pages.push(<span key="ellipsis1" style={ellipsisStyle}>...</span>);
            }

            const start = Math.max(2, currentPage - 1);
            const end = Math.min(last_page - 1, currentPage + 1);

            for (let i = start; i <= end; i++) {
                pages.push(
                    <button
                        key={i}
                        onClick={() => handlePageChange(i)}
                        style={{
                            ...pageButtonStyle,
                            backgroundColor: currentPage === i ? 'var(--color-primary)' : '#f8f9fa',
                            color: currentPage === i ? 'white' : '#333'
                        }}
                    >
                        {i}
                    </button>
                );
            }

            if (currentPage < last_page - 2) {
                pages.push(<span key="ellipsis2" style={ellipsisStyle}>...</span>);
            }

            // Always show last page
            if (last_page > 1) {
                pages.push(
                    <button
                        key={last_page}
                        onClick={() => handlePageChange(last_page)}
                        style={{
                            ...pageButtonStyle,
                            backgroundColor: currentPage === last_page ? 'var(--color-primary)' : '#f8f9fa',
                            color: currentPage === last_page ? 'white' : '#333'
                        }}
                    >
                        {last_page}
                    </button>
                );
            }
        }

        return pages;
    };

    return (
        <ErrorBoundary>
            <div style={{ background: '#f8fafc', minHeight: '100vh' }}>
                {/* Header Section */}
                <div style={{
                    background: 'linear-gradient(135deg, var(--color-primary) 0%, var(--color-secondary) 100%)',
                    color: 'white',
                    padding: '3rem 1rem',
                    textAlign: 'center'
                }}>
                    <h1 style={{
                        fontSize: '2.5rem',
                        fontWeight: '700',
                        marginBottom: '0.5rem',
                        textShadow: '2px 2px 4px rgba(0,0,0,0.3)',
                        lineHeight: '1.2'
                    }}>
                        Specialitățile Medicale
                    </h1>
                    <p style={{
                        fontSize: '1.1rem',
                        opacity: '0.9',
                        marginBottom: '0',
                        textShadow: '1px 1px 3px rgba(0,0,0,0.3)',
                        lineHeight: '1.4'
                    }}>
                        Explorează toate specialitățile medicale disponibile (SPS API)
                    </p>
                </div>

                {/* Content Section */}
                <div style={{
                    maxWidth: '1400px',
                    margin: '0 auto',
                    padding: '2rem 1rem'
                }}>

                    {/* Error Message */}
                    {error && (
                        <div style={{
                            background: '#ffebee',
                            color: '#c62828',
                            padding: '1rem',
                            borderRadius: '8px',
                            marginBottom: '2rem',
                            border: '1px solid #ffcdd2'
                        }}>
                            <strong>Eroare:</strong> {error}
                        </div>
                    )}

                    {/* Search and Filter Controls */}
                    <div style={{
                        background: 'white',
                        padding: '2rem',
                        borderRadius: '12px',
                        marginBottom: '2rem',
                        boxShadow: '0 4px 20px rgba(0,0,0,0.08)'
                    }}>
                        <div style={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
                            gap: '1rem',
                            marginBottom: '1.5rem'
                        }}>
                            {/* Search Input */}
                            <div>
                                <label style={{
                                    display: 'block',
                                    marginBottom: '0.5rem',
                                    fontWeight: '600',
                                    color: '#333'
                                }}>
                                    Caută specialitate:
                                </label>
                                <input
                                    type="text"
                                    value={searchQuery}
                                    onChange={handleSearch}
                                    placeholder="Ex: cardiologie, oftalmologie..."
                                    style={{
                                        width: '100%',
                                        padding: '0.75rem',
                                        border: '1px solid #ddd',
                                        borderRadius: '8px',
                                        fontSize: '1rem',
                                        backgroundColor: 'white'
                                    }}
                                />
                            </div>

                            {/* Sort Options */}
                            <div>
                                <label style={{
                                    display: 'block',
                                    marginBottom: '0.5rem',
                                    fontWeight: '600',
                                    color: '#333'
                                }}>
                                    Sortează după:
                                </label>
                                <select
                                    value={sortOrder}
                                    onChange={(e) => handleSortChange(e.target.value)}
                                    style={{
                                        width: '100%',
                                        padding: '0.75rem',
                                        border: '1px solid #ddd',
                                        borderRadius: '8px',
                                        fontSize: '1rem',
                                        backgroundColor: 'white'
                                    }}
                                >
                                    <option value="denumire">Nume (A-Z)</option>
                                    <option value="-denumire">Nume (Z-A)</option>
                                    <option value="id">ID crescător</option>
                                    <option value="-id">ID descrescător</option>
                                </select>
                            </div>
                        </div>

                        {/* Actions */}
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <button
                                onClick={clearFilters}
                                style={{
                                    padding: '0.5rem 1rem',
                                    background: '#f8f9fa',
                                    color: '#666',
                                    border: '1px solid #ddd',
                                    borderRadius: '6px',
                                    cursor: 'pointer',
                                    fontSize: '0.9rem'
                                }}
                            >
                                Curăță filtre
                            </button>

                            {pagination && (
                                <div style={{ fontSize: '0.9rem', color: '#666' }}>
                                    {pagination.total > 0 ? (
                                        <>Afișate {specialties.length} specialități din {pagination.total} rezultate</>
                                    ) : (
                                        <>Nu s-au găsit specialități</>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Loading State — skeleton cards */}
                    {loading && (
                        <div className="sn-skeleton-grid">
                            {Array.from({ length: 6 }).map((_, i) => (
                                <div key={i} className="sn-skeleton-card">
                                    <div className="sk-header">
                                        <div className="skeleton sk-avatar" />
                                        <div className="sk-lines">
                                            <div className="skeleton sk-line-lg" />
                                            <div className="skeleton sk-line-sm" />
                                        </div>
                                    </div>
                                    <div className="skeleton sk-box" />
                                </div>
                            ))}
                        </div>
                    )}

                    {/* Specialties Grid */}
                    {!loading && specialties.length > 0 && (
                        <div style={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
                            gap: '1.5rem',
                            marginBottom: '2rem'
                        }}>
                            {specialties.map((specialty) => (
                                <div
                                    key={specialty.id}
                                    style={{
                                        background: 'white',
                                        padding: '1.5rem',
                                        borderRadius: '12px',
                                        boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                                        border: '1px solid #e0e0e0',
                                        transition: 'all 0.3s ease',
                                        cursor: 'pointer'
                                    }}
                                    onMouseEnter={(e) => {
                                        e.target.style.transform = 'translateY(-5px)';
                                        e.target.style.boxShadow = '0 8px 30px rgba(0,0,0,0.15)';
                                    }}
                                    onMouseLeave={(e) => {
                                        e.target.style.transform = 'translateY(0)';
                                        e.target.style.boxShadow = '0 4px 20px rgba(0,0,0,0.08)';
                                    }}
                                >
                                    <div style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        marginBottom: '1rem'
                                    }}>
                                        <div style={{
                                            width: '50px',
                                            height: '50px',
                                            borderRadius: '50%',
                                            background: 'linear-gradient(135deg, var(--color-primary), var(--color-secondary))',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            fontSize: '1.5rem',
                                            color: 'white',
                                            marginRight: '1rem',
                                            fontWeight: 'bold'
                                        }}>
                                            {specialty.denumire.charAt(0).toUpperCase()}
                                        </div>
                                        <div style={{ flex: 1 }}>
                                            <h3 style={{
                                                margin: '0 0 0.25rem 0',
                                                color: 'var(--color-primary)',
                                                fontSize: '1.2rem',
                                                fontWeight: '600',
                                                lineHeight: '1.2'
                                            }}>
                                                {specialty.denumire}
                                            </h3>
                                            <p style={{
                                                margin: '0',
                                                color: '#666',
                                                fontSize: '0.85rem'
                                            }}>
                                                ID: {specialty.id}
                                            </p>
                                        </div>
                                    </div>

                                    <div style={{
                                        background: '#f8fafc',
                                        padding: '1rem',
                                        borderRadius: '8px',
                                        border: '1px solid #e0e0e0'
                                    }}>
                                        <p style={{
                                            margin: '0',
                                            color: '#333',
                                            fontSize: '0.9rem',
                                            textAlign: 'center'
                                        }}>
                                            Medical specialty - {specialty.denumire}
                                        </p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}

                    {/* Empty State */}
                    {!loading && specialties.length === 0 && searchQuery && (
                        <div className="sn-empty">
                            <div className="sn-empty-icon">
                                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 21l-4.35-4.35M17 11A6 6 0 1 1 5 11a6 6 0 0 1 12 0z" />
                                </svg>
                            </div>
                            <h3>Nicio specialitate găsită</h3>
                            <p>Nu s-au găsit specialități care să corespundă căutării "{searchQuery}".</p>
                            <button className="sn-empty-btn" onClick={clearFilters}>
                                Curăță căutarea
                            </button>
                        </div>
                    )}

                    {/* Pagination */}
                    {pagination && pagination.last_page > 1 && (
                        <div style={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            gap: '0.5rem',
                            marginTop: '2rem'
                        }}>
                            <button
                                onClick={() => handlePageChange(page - 1)}
                                disabled={page <= 1}
                                style={{
                                    ...pageButtonStyle,
                                    opacity: page <= 1 ? 0.5 : 1,
                                    cursor: page <= 1 ? 'not-allowed' : 'pointer'
                                }}
                            >
                                ‹ Anterior
                            </button>

                            {renderPageNumbers()}

                            <button
                                onClick={() => handlePageChange(page + 1)}
                                disabled={page >= pagination.last_page}
                                style={{
                                    ...pageButtonStyle,
                                    opacity: page >= pagination.last_page ? 0.5 : 1,
                                    cursor: page >= pagination.last_page ? 'not-allowed' : 'pointer'
                                }}
                            >
                                Următor ›
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </ErrorBoundary>
    );
}

// Page button styles
const pageButtonStyle = {
    padding: '0.5rem 0.75rem',
    border: '1px solid #ddd',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '0.9rem',
    fontWeight: '500',
    transition: 'all 0.3s ease',
    minWidth: '32px',
    textAlign: 'center'
};

const ellipsisStyle = {
    padding: '0 0.5rem',
    color: '#666',
    fontSize: '0.9rem'
};

export default SpecialitatiNew;
